import React, { useEffect, useState } from 'react';
import { ExchangeBidData } from '../../models/exchangeBidData';
import { GenerationData } from '../../models/generationData';
import { ConsumptionData } from '../../models/consumption';
import { lightenColor, ReMixChart } from '../../components/recharts/ReMixCharts';
import { BrushStart, ChartType } from '../../models/chart_model';
import { getCollectiveData, getConsumptionData, getGenerationData } from '../../Rest_api/restapi';
import { Brush } from 'recharts';
import { COLOR_CODES } from '../../common';
import { ChartData, ChartDataParams, FormatChartData } from '../../components/recharts/functions';
import { SearchBox } from '../../components/Search';
import { titleCase } from '../../extensions/extension';
import { FilterExchangeData } from './fill_missing';
import { FilterTab } from '../../components/Filters';
import { ChartTable } from '../../components/table/SummaryTable';
const views = ['Chart', 'Table'];

export function Home({startDate, endDate}: {startDate: string, endDate: string}) {
    const [collectiveData, setCollectiveData] = useState<ExchangeBidData[]>([]);
    const [generationData, setGenerationData] = useState<GenerationData[]>([]);
    const [consumptionData, setConsumptionData] = useState<ConsumptionData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStates, setLoadingStates] = useState({
        collective: false,
        consumption: false,
        generation: false
    });

    useEffect(() => {
        setIsLoading(true);
        setLoadingStates({
            collective: true,
            consumption: true,
            generation: true
        });

        // Fetch collective data
        getCollectiveData(startDate, endDate).then((data) => {
            // sort data by slot and date
            data.sort((a, b) => {
                if (a.date === b.date) {
                    return a.time_slot - b.time_slot
                }
                return a.date.localeCompare(b.date);
            });
            setCollectiveData(data);
            setLoadingStates(prev => ({ ...prev, collective: false }));
        }).catch((error) => {
            console.error('Error fetching collective data:', error);
            setLoadingStates(prev => ({ ...prev, collective: false }));
        });
        
        // Fetch consumption data
        getConsumptionData(startDate, endDate).then((data) => {
            setConsumptionData(data);
            setLoadingStates(prev => ({ ...prev, consumption: false }));
        }).catch((error) => {
            console.error('Error fetching consumption data:', error);
            setLoadingStates(prev => ({ ...prev, consumption: false }));
        });

        // Fetch generation data
        getGenerationData(startDate, endDate).then((data) => {
            setGenerationData(data);
            setLoadingStates(prev => ({ ...prev, generation: false }));
        }).catch((error) => {
            console.error('Error fetching generation data:', error);
            setLoadingStates(prev => ({ ...prev, generation: false }));
        });
    }, [startDate, endDate]);

    // Check if all data is loaded
    useEffect(() => {
        const allLoaded = !loadingStates.collective && !loadingStates.consumption && !loadingStates.generation;
        setIsLoading(!allLoaded);
    }, [loadingStates]);
    
  return (
    <div className="h-full w-full flex flex-col bg-gray-50 p-3 overflow-scroll lg:min-h-0 lg:overflow-hidden min-h-[1900px]">
      {/* 2x2 Flex Layout - Full Screen */}
      <div className="flex flex-col lg:flex-row flex-1 gap-3 min-h-0">
        {/* Left Column */}
        <div className="flex flex-col flex-1 gap-3 min-h-0">
          {/* Card 1 */}
          <div className="bg-white rounded-xl shadow-md border p-3 border-gray-200 hover:shadow-lg transition-shadow duration-300 flex-1 min-h-0">
            <CollectiveChart 
              collectiveData={FilterExchangeData(collectiveData, {exchangeIndex: 0, exchangeProductIndex: 0})} 
              title="IEX DAM"
              loading={loadingStates.collective}
            />
          </div>

          {/* Card 3 */}
          <div className="bg-white rounded-xl shadow-md p-3 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex-1 min-h-0 lg:min-h-0">
            <div className="text-left h-full w-full flex flex-col">
              {loadingStates.consumption ? (
                <LoadingSpinner message="Loading consumption data..." />
              ) : (
                <DemandChart demandData={consumptionData} />
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col flex-1 gap-3 min-h-0">
          {/* Card 2 */}
          <div className="bg-white rounded-xl shadow-md p-3 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex-1 min-h-0">
            <CollectiveChart 
              collectiveData={FilterExchangeData(collectiveData, {exchangeIndex: 0, exchangeProductIndex: 3})} 
              title="IEX RTM"
              loading={loadingStates.collective}
            />
          </div>

          {/* Card 4 */}
          <div className="bg-white rounded-xl shadow-md p-3 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex-1 min-h-0">
              <div className="text-left h-full w-full flex flex-col">
              {loadingStates.generation ? (
                <LoadingSpinner message="Loading generation data..." />
              ) : (
                <GenerationChart generationData={generationData} />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="flex-shrink-0 mt-2">
        <div className="text-lef text-xs" style={{ color: COLOR_CODES.secondary }}>
        Data Source: <a 
            href="https://www.iexindia.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            IEX
          </a>, <a 
            href="http://npp.gov.in" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            NPP
          </a>, <a 
            href="https://cea.nic.in" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            CEA
          </a>, <a 
            href="https://grid-india.in/en/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            Grid India
          </a>
        </div>
      </div>
    </div>
  );
};

// Loading Spinner Component
function LoadingSpinner({ message }: { message: string }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600 text-sm font-medium">{message}</p>
    </div>
  );
}

export function CollectiveChart({collectiveData, title, loading}: {collectiveData: ExchangeBidData[], title?: string, loading?: boolean}) {
  const [chartData, setChartData] = useState<ChartData>({
    keys: [],
    unit_keys: [],
    keysLabels: [],
    group_by: {},
    data: [],
  });
  const AVAILABLE_INTERVALS = [
    {
      key: ['time_slot', 'date'],
      label: 'Slot Wise',
      volKey: 'mcv_mw',
      time_slot:true,
      purchaseKey: 'purchase_bid_mw',
      sellKey: 'sell_bid_mw',
      volUnit: 'MW',
    },
    {
      key: ['date'],
      label: 'Date Wise',
      volKey: 'mcv_mwh',
      time_slot:false,
      purchaseKey: 'purchase_bid_mwh',
      sellKey: 'sell_bid_mwh',
      volUnit: 'Million MWh',
    }
  ]
  const [selectedView, setSelectedView] = useState(views[0]);
  const [selectedInterval, setSelectedInterval] = useState(AVAILABLE_INTERVALS[0]);
  useEffect(() => {
    const par: ChartDataParams = {
      data: collectiveData,
      keys: selectedInterval.key,
      volumeKey: selectedInterval.volKey,
      priceKey: 'wt_mcp_rs_mwh',
      groupBy: [],
      aggregationKeys: [selectedInterval.purchaseKey, selectedInterval.sellKey],
    }
    const data = FormatChartData(par);
    
    // Convert MWh to Million MWh for day-wise view
    if (!selectedInterval.time_slot) {
      const convertedData = {
        ...data,
        data: data.data.map(item => {
          const convertedItem = { ...item };
          Object.keys(convertedItem).forEach(key => {
            if (key.includes('_mcv_mwh') && typeof convertedItem[key] === 'number') {
              convertedItem[key] = convertedItem[key] / 1000000; // Convert to Million MWh
            }
            if (key.includes('_purchase_bid_mwh') && typeof convertedItem[key] === 'number') {
              convertedItem[key] = convertedItem[key] / 1000000; // Convert to Million MWh
            }
            if (key.includes('_sell_bid_mwh') && typeof convertedItem[key] === 'number') {
              convertedItem[key] = convertedItem[key] / 1000000; // Convert to Million MWh
            }
          });
          return convertedItem;
        })
      };
      console.log("Collective Data (converted) = ", convertedData);
      setChartData(convertedData);
    } else {
      console.log("Collective Data = ", data);
      setChartData(data);
    }
  }, [selectedInterval, collectiveData]);
  
    return (
        <div className="text-left h-full w-full flex flex-col ">
          <div className='flex flex-row justify-between gap-x-2'>
            {title && <h3 className="text-xl font-semibold text-gray-800 flex-shrink-0 chartHeading lg:w-[100px]">{title}</h3>}
            <div className='flex flex-row gap-x-2 pb-2'>
            <FilterTab
                options={AVAILABLE_INTERVALS.map((item) => item.label)}
                selected={[selectedInterval.label]}
                onClick={(e) => setSelectedInterval(AVAILABLE_INTERVALS.find((item) => item.label === e) as any)}
            /></div>
            <div className='flex flex-row gap-x-2 pb-2'>
              <FilterTab
                options={views}
                selected={[selectedView]}
                onClick={(e) => setSelectedView(e)}
              />
            </div>
            </div>
            <div className="flex-1 min-h-0">
                {loading ? (
                    <LoadingSpinner message={`Loading ${title} data...`} />
                ) : (
                    <div className='h-full w-full flex flex-col'>
                        <div className="flex-1 min-h-0">
                          {selectedView === 'Chart' && (
                            <ReMixChart
                                data={chartData.data}
                                xDataKey={selectedInterval.time_slot ? "time_slot" : "date"}
                                isTimeSlot={selectedInterval.time_slot}
                                secondXDataKey={selectedInterval.time_slot ? "date" : undefined}
                                yAxisLabel={selectedInterval.volUnit}
                                secondYAxisLabel='Rs/MWh'
                                yAxisWidth={50}
                                unit={''}

                                secondYAxisWidth={45}
                                onlyTitle={selectedInterval.time_slot ? false : true}
                                brushStart={
                                    BrushStart.End
                                }
                                // fontSize={12}
                                
                                showBrush={true}
                                brushIndex={
                                  selectedInterval.key.includes('time_slot') ? {
                                    startIndex: chartData.data.length-96,
                                    endIndex: chartData.data.length - 1
                                  } : {
                                    startIndex: 0,
                                    endIndex: chartData.data.length - 1
                                }}
                                syncid={'collective-' + selectedInterval.label}

                                
                                legends={getLegends()}
                                
                            />
                            )}
                            {selectedView === 'Table' && (
                              <ChartTable
                                data={chartData.data}
                                legends={getLegends()}
                                unit={selectedInterval.volUnit}
                                extraKeys={selectedInterval.time_slot ? ['time_slot', 'date'] : ['date']}
                                extraLabels={selectedInterval.time_slot ? ['Time Slot', 'Date'] : ['Date']}
                              />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
    function getLegends(){
      return [
        {
          name: 'MCV',
          dataKey: '_' + selectedInterval.volKey,
          stroke: COLOR_CODES.secondary,
          unit: selectedInterval.volUnit,
          type: ChartType.Bar
      },
          {
              name: 'Wt. Avg Price',
              dataKey:'_' + 'wt_mcp_rs_mwh',
              stroke: COLOR_CODES.primary,
              yAxisId: 'right',
              unit: 'Rs/MWh',
              type: ChartType.Line,
          },


          {
              name: 'Sell Bid',
              dataKey: '_' + selectedInterval.sellKey,
              color: 'green',
              type: ChartType.Line,
              unit: selectedInterval.volUnit,
          },
          {
              name: 'Purchase Bid',
              dataKey:'_' + selectedInterval.purchaseKey,
              color: 'yellow',
              type: ChartType.Line,
              unit: selectedInterval.volUnit,
          },
          
      ];
    }
}

export function DemandChart({demandData}: {demandData: ConsumptionData[]}) {
    const [selectedStates, setSelectedStates] = useState<string[]>([]);
    const [selectedView, setSelectedView] = useState(views[0]);
    const [chartData, setChartData] = useState<ChartData>({
        keys: [],
        unit_keys: [],
        keysLabels: [],
        group_by: {},
        data: [],
    });
    useEffect(() => {
        // Filter out BHUTAN from the data
        let filteredData = demandData.filter((item) => item.states !== 'BHUTAN');
        
        if(selectedStates.length > 0){
            filteredData = filteredData.filter((item) => selectedStates.includes(item.states));
        }
        const par: ChartDataParams = {
            data: filteredData,
            keys: ['date'],
            volumeKey: 'energy_met_mu',
            groupBy: selectedStates.length > 0 ? ['states'] : [],
            aggregationKeys: selectedStates.length > 0 ? [] : [],
        }
        const data = FormatChartData(par);
        
        // Convert from MUs (Million KWh) to Million MWh by dividing by 1000
        const convertedData = {
            ...data,
            data: data.data.map(item => {
                const convertedItem = { ...item };
                Object.keys(convertedItem).forEach(key => {
                    if (key.includes('_energy_met_mu') && typeof convertedItem[key] === 'number') {
                        convertedItem[key] = convertedItem[key] / 1000;
                    }
                });
                return convertedItem;
            })
        };
        
        console.log("Consumption Data = ", convertedData);
        setChartData(convertedData);
    }, [selectedStates, demandData]);
    return (
        <>
        <div className='flex flex-row justify-between gap-x-2'>
        <h3 className="text-xl font-semibold text-gray-800 flex-shrink-0 chartHeading">Consumption Analytics</h3>
        <div className='flex flex-row gap-x-2 pb-2'>
              <FilterTab
                options={views}
                selected={[selectedView]}
                onClick={(e) => setSelectedView(e)}
              />
            </div>
        {/* </div> */}
        </div>
        <div className="flex-1 min-h-0">

        <div className='h-full w-full flex flex-col'>
            <div className="flex-1 min-h-0">
                {selectedView === 'Chart' && (
                <ReMixChart 
                data={chartData.data}
                xDataKey='date'
                isTimeSlot={false}
                yAxisLabel='Million MWh'
                unit='Million MWh'
                onlyTitle={true}
                yAxisWidth={45}
                secondYAxisWidth={20}
                showSecondYAxis={false}
                legends={
                    
                chartData.keys.map((key) => ({
                    name: key + ' Total Volume',
                    dataKey: key + '_energy_met_mu',
                    color: 'blue',
                    unit: 'Million MWh',
                    type: ChartType.Line,
                }))
            
            }
                brushStart={BrushStart.End}
                showBrush={true}
                syncid='demand'
                />
                )}
                {selectedView === 'Table' && (
                    <ChartTable
                        data={chartData.data}
                        legends={chartData.keys.map((key) => ({
                          name: key + ' Total Volume',
                          dataKey: key + '_energy_met_mu',
                          color: 'blue',
                          unit: 'Million MWh',
                          type: ChartType.Line,
                      }))}
                        extraKeys={['date']}
                        extraLabels={['Date']}
                    />
                )}
            </div>
            </div>
            </div>
       </>
        
    )
}



export function GenerationChart({generationData}: {generationData: GenerationData[]}) {
    const [selectedStates, setSelectedStates] = useState<string[]>([]);
    const [chartData, setChartData] = useState<ChartData>({
        keys: [],
        unit_keys: [],
        keysLabels: [],
        group_by: {},
        data: [],
    });
    const GENERATION_SOURCE = {
        'thermal': COLOR_CODES.primary,
        'hydro': COLOR_CODES.secondary,
        'nuclear': 'green',
        'solar': 'orange',
        'wind': 'blue',
        'other': 'purple',
    }
    const list = Object.entries(GENERATION_SOURCE).map(([key, stroke]) => ({
        key,
        stroke,
    }));
    const [selectedView, setSelectedView] = useState(views[0]);
    useEffect(() => {
        // Filter out BHUTAN and only include specified generation sources
        let filteredData = generationData.filter((item) => 
            item.state !== 'BHUTAN' && 
            Object.keys(GENERATION_SOURCE).includes(item.power_type.toLowerCase())
        );
        
        if(selectedStates.length > 0){
            filteredData = filteredData.filter((item) => selectedStates.includes(item.state));
        }
        const par: ChartDataParams = {
            data: filteredData,
            keys: ['date'],
            volumeKey: 'generation_mu_actual',
            groupBy: ['power_type'],
            aggregationKeys: [],
        }
        const data = FormatChartData(par);
        
        // Convert from MUs (Million KWh) to Million MWh by dividing by 1000
        const convertedData = {
            ...data,
            data: data.data.map(item => {
                const convertedItem = { ...item };
                Object.keys(convertedItem).forEach(key => {
                    if (key.includes('_generation_mu_actual') && typeof convertedItem[key] === 'number') {
                        convertedItem[key] = convertedItem[key] / 1000;
                    }
                });
                return convertedItem;
            })
        };
        
        console.log("Generation Data = ", convertedData);
        setChartData(convertedData);
    }, [selectedStates, generationData]);
    return (
        <>
        <div className='flex flex-row justify-between gap-x-2'>
        <h3 className="text-xl font-semibold text-gray-800 flex-shrink-0 chartHeading">Generation Analytics</h3>
        <div className='flex flex-row gap-x-2 pb-2'>
              <FilterTab
                options={views}
                selected={[selectedView]}
                onClick={(e) => setSelectedView(e)}
              />
            </div>
        {/* <SearchBox
        placeholder='Search by State'
        options={generationData.filter((item, index, self) =>
            index === self.findIndex((t) => t.state === item.state) && item.state !== 'BHUTAN'
        )   .map((item) => ({
            label: item.state,
            value: item.state
        }))}
        selectedOptions={selectedStates.map((item) => ({
            label: item,
            value: item
        }))}
        onChange={(e) => setSelectedStates(e.map((item) => item.value as string))}
        isMany={true}
        /> */}
        </div>
        <div className="flex-1 min-h-0">

        <div className='h-full w-full flex flex-col'>
            <div className="flex-1 min-h-0">
                {selectedView === 'Chart' && (
                <ReMixChart 
                data={chartData.data}
                xDataKey='date'
                // isTimeSlot={false}
                yAxisLabel='Million MWh'
                // showSecondYAxis={false}
                secondYAxisWidth={20}
                yAxisWidth={45}
                onlyTitle={true}
                unit='Million MWh'
                legends={
                    
                chartData.keys.filter((key) => key !== '0').map((key) => ({
                    name: titleCase(key),
                    dataKey: key + '_generation_mu_actual',
                    stroke: list.find((item) => item.key.toLowerCase() === key.toLowerCase())?.stroke,
                    unit: 'Million MWh',
                    type: ChartType.Area,
                    stackId: 'a',
                })).sort((a, b) => 
                // sort by order of list above for Generation Source
                list.findIndex((item) => item.key.toLowerCase() === a.name.toLowerCase()) - list.findIndex((item) => item.key.toLowerCase() === b.name.toLowerCase())
                ).reverse()
            
            }
                brushStart={BrushStart.End}
                showBrush={true}
                syncid='generation'
                />
                )}
                {selectedView === 'Table' && (
                    <ChartTable
                        data={chartData.data}
                        legends={chartData.keys.map((key) => ({
                            name: titleCase(key),
                            dataKey: key + '_generation_mu_actual',
                            color: 'blue',
                            unit: 'Million MWh',
                            type: ChartType.Line,
                        }))}
                        extraKeys={['date']}
                        extraLabels={['Date']}
                    />
                )}
              </div>
            </div>
            </div>
       </>
        
    )
}

export default Home;
